/**
 * Moving the videos from the reader's other Visious tabs into this one: the header's control, the
 * confirmation it opens, and what a tab says when the browser would not let it close.
 *
 * **Nothing here promises that a tab will close**, which is the section's one rule. The browser has
 * the last word on that (`tabs/useTabImport.ts`), so the confirmation says the tabs close where they
 * can and the one sentence afterwards is reserved for the tabs that are still open.
 */
export const tabs = {
  /** The control beside the theme switch. Its hover word and its accessible name are the same act. */
  open: 'Move queued videos from other tabs to this one',

  /**
   * Nobody answered, so there is nothing to confirm. One line under the control instead of a dialog
   * the reader would have to dismiss for nothing.
   */
  empty: 'There are no other tabs with videos.',

  ask: {
    title: 'Move videos from other tabs',
    detail:
      'The videos from your other Visious tabs will be moved to this tab’s queue, and those tabs will be closed if possible.',
    /**
     * The one thing about *this* tab the reader has to know before choosing, because it decides
     * whether the playlist they are watching through stays in the list.
     */
    linked:
      'This tab’s queue follows a playlist. Unlinking it first keeps that playlist’s videos in the queue.',
    confirm: 'Move',
    unlink: 'Unlink and move',
    cancel: 'Cancel',
    /** `role="status"` beside the spinner while the other tabs are answering. */
    working: 'Asking your other tabs.',
    close: 'Close',
  },

  /** The only outcome worth a sentence. Every tab closing is silent. */
  stayed: 'Some tabs could not be closed and have to be closed by hand.',

  /**
   * The watch page of a tab that gave its videos up and stayed open. It takes the player's own box,
   * so it reads like a video that will not play, and it offers nothing: there is nothing to try
   * again and nowhere else to watch a video that is now in another tab.
   */
  handedOver: {
    title: 'The videos moved to another tab',
    detail: 'Your browser did not allow this tab to close. Please close it yourself.',
  },
} as const;
